/**
 * Vitals, patient history and lab report assistance (frontend §5, §6, §7).
 *
 * The requirement that shapes all three: "Only display data actually available.
 * Do not fabricate missing values." So each section renders one of three
 * states, and they look different on purpose —
 *
 *   connected, with data     the readings, each with its source and timestamp
 *   connected, no data       "no records", which is a real clinical answer
 *   not connected            a dashed placeholder saying so
 *
 * A blank space where a reading should be is the one thing none of them do.
 */

import type { CopilotContext, LabResult, VitalReading } from "@/types/copilot";

import { AiBadge, DataRow, SectionEmpty, SectionUnavailable } from "./copilot-primitives";

const VITAL_LABEL: Record<string, string> = {
  heartRate: "Heart rate",
  spo2: "SpO₂",
  bloodPressure: "Blood pressure",
  temperature: "Temperature",
  glucose: "Glucose",
  sleep: "Sleep",
  activity: "Activity",
};

const relative = (iso: string | null) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

export function VitalsSection({ context }: { context: CopilotContext | undefined }) {
  if (!context || context.vitalsSection?.unavailable) {
    return <SectionUnavailable reason={context?.vitalsSection?.reason} />;
  }
  const vitals = context.vitals ?? [];
  if (vitals.length === 0) {
    return (
      <SectionEmpty>No device or vitals data has been recorded for this patient.</SectionEmpty>
    );
  }

  return (
    <div>
      {context.fixtures && (
        <p className="mb-2 rounded-md bg-warning/10 px-2 py-1 text-[0.65rem] font-medium text-warning">
          Development fixtures — not real patient readings.
        </p>
      )}
      {vitals.map((vital: VitalReading, index) => (
        <DataRow
          key={`${vital.kind}-${index}`}
          label={vital.label ?? VITAL_LABEL[vital.kind] ?? vital.kind}
          value={`${vital.value}${vital.unit ? ` ${vital.unit}` : ""}`}
          meta={[vital.source, relative(vital.recordedAt)].filter(Boolean).join(" · ")}
        />
      ))}
    </div>
  );
}

export function HistorySection({ context }: { context: CopilotContext | undefined }) {
  const history = context?.history;
  if (!history || history.unavailable) return <SectionUnavailable reason={history?.reason} />;

  const data = history.data as Record<string, unknown> | undefined;
  const groups = data ? Object.entries(data).filter(([, value]) => hasContent(value)) : [];

  if (groups.length === 0) {
    return <SectionEmpty>No previous records are on file for this patient.</SectionEmpty>;
  }

  return (
    <div className="space-y-2.5">
      {groups.map(([key, value]) => (
        <div key={key}>
          <p className="text-[0.65rem] font-semibold tracking-wide text-muted-foreground uppercase">
            {humanise(key)}
          </p>
          <div className="mt-1 space-y-1">
            {toLines(value).map((line, index) => (
              <p key={index} className="text-xs leading-relaxed text-foreground">
                {line}
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Lab report assistance (§7).
 *
 * The patient's value and the reference range are the lab's; the observation is
 * the AI's and is marked as such. "The doctor remains responsible for
 * interpretation" is a line in the spec and a line in this UI — the observation
 * never replaces the number it sits beneath.
 */
export function LabsSection({ context }: { context: CopilotContext | undefined }) {
  if (!context || context.labsSection?.unavailable) {
    return <SectionUnavailable reason={context?.labsSection?.reason} />;
  }
  const labs = context.labs ?? [];
  if (labs.length === 0) {
    return <SectionEmpty>No lab reports are available for this patient.</SectionEmpty>;
  }

  return (
    <div className="space-y-2">
      {labs.map((lab: LabResult, index) => (
        <div
          key={lab.id ?? `${lab.testName}-${index}`}
          className="rounded-lg border border-border bg-card px-3 py-2"
        >
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-xs font-semibold">{lab.testName}</p>
            <p className="shrink-0 text-xs font-semibold tabular-nums">
              {lab.value ?? "—"}
              {lab.unit ? ` ${lab.unit}` : ""}
            </p>
          </div>

          <p className="mt-0.5 text-[0.65rem] text-muted-foreground">
            {lab.referenceRange ? `Reference ${lab.referenceRange}` : "No reference range supplied"}
            {lab.reportedAt ? ` · ${new Date(lab.reportedAt).toLocaleDateString()}` : ""}
            {lab.source ? ` · ${lab.source}` : ""}
          </p>

          {lab.aiObservation && (
            <div className="mt-2 rounded-md bg-info/8 px-2 py-1.5">
              <AiBadge className="mb-1" />
              <p className="text-[0.7rem] leading-relaxed text-foreground">{lab.aiObservation}</p>
            </div>
          )}
        </div>
      ))}

      <p className="pt-1 text-[0.65rem] leading-relaxed text-muted-foreground">
        The laboratory report is the authoritative result. Any observation above is an AI note for
        your consideration.
      </p>
    </div>
  );
}

/* ── helpers ────────────────────────────────────────────────────────────── */

function hasContent(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

const humanise = (key: string) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

/** Renders an unknown history payload without assuming its shape. */
function toLines(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(describe);
  if (typeof value === "object" && value !== null) {
    return Object.entries(value).map(([key, entry]) => `${humanise(key)}: ${describe(entry)}`);
  }
  return [describe(value)];
}

function describe(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) return value.map(describe).join(", ");
  const record = value as Record<string, unknown>;
  // Prefer a human-meaningful field over dumping the whole object.
  for (const key of ["name", "title", "diagnosis", "description", "label"]) {
    if (typeof record[key] === "string") {
      const date = record["date"] ?? record["reportedAt"] ?? record["createdAt"];
      return typeof date === "string"
        ? `${record[key]} (${new Date(date).toLocaleDateString()})`
        : (record[key] as string);
    }
  }
  return Object.entries(record)
    .map(([key, entry]) => `${humanise(key)}: ${describe(entry)}`)
    .join(", ");
}
